import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
} from '@material-ui/core';
import {requestMfaCode, verifyMfaCode, completeLogin} from 'util/Authentication';

const ozgeneBlue = '#3357b8';
const RESEND_COOLDOWN_SECONDS = 30;
const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [localPart, domain] = email.split('@');

  if (localPart.length <= 1) return email;

  return `${localPart[0]}***@${domain}`;
};

export default function MfaDialog({open, onClose, idToken, email, onClearPassword}) {
  const [mfaCode, setMfaCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const timerRef = useRef(null);
  const maskedEmail = maskEmail(email);

  // Start countdown when dialog opens
  useEffect(() => {
    if (open) {
      setCountdown(RESEND_COOLDOWN_SECONDS);
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);

            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open]);
  const handleCodeChange = ({target: {value}}) => {
    if (/^\d{0,6}$/.test(value)) {
      setMfaCode(value);
      setErrorMessage('');
    }
  };
  const handleResendCode = async () => {
    setIsResending(true);
    setErrorMessage('');
    setMfaCode('');
    try {
      await requestMfaCode(idToken);
      setErrorMessage('');
      // Reset countdown after successful resend
      setCountdown(RESEND_COOLDOWN_SECONDS);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);

            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };
  const handleVerify = async () => {
    if (mfaCode.length !== 6) {
      setErrorMessage('Please enter a 6-digit code');

      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      await verifyMfaCode(idToken, mfaCode);
      completeLogin(idToken);
    } catch (err) {
      setErrorMessage(err.message || 'Invalid or expired verification code');
      setMfaCode('');
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = () => {
    setMfaCode('');
    setErrorMessage('');
    onClearPassword();
    onClose();
  };
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && mfaCode.length === 6 && !isLoading) {
      handleVerify();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Typography variant="h6" style={{fontWeight: 600}}>
            Email Verification
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            A verification code has been sent to your email:
          </Typography>
          <Typography
            variant="body1"
            style={{fontWeight: 600, color: ozgeneBlue, marginBottom: '16px'}}
          >
            {maskedEmail}
          </Typography>
        </Box>

        {errorMessage && (
          <Typography variant="body2" style={{color: 'red', marginBottom: '16px'}}>
            {errorMessage}
          </Typography>
        )}

        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label="Verification Code"
          placeholder="Enter 6-digit code"
          value={mfaCode}
          onChange={handleCodeChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              fontSize: '24px',
              letterSpacing: '8px',
              fontWeight: 600,
            },
          }}
        />

        <Box mt={2} display="flex" justifyContent="center">
          <Button
            onClick={handleResendCode}
            disabled={isResending || isLoading || countdown > 0}
            style={{color: countdown > 0 ? '#999' : ozgeneBlue, textTransform: 'none'}}
          >
            {isResending ? (
              <>
                <CircularProgress size={16} style={{marginRight: 8}} />
                Sending...
              </>
            ) : countdown > 0 ? (
              `Resend code in ${countdown}s`
            ) : (
              "Didn't receive the code? Resend"
            )}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions style={{padding: '16px 24px'}}>
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={mfaCode.length !== 6 || isLoading}
          style={{
            backgroundColor: mfaCode.length === 6 ? ozgeneBlue : undefined,
            color: mfaCode.length === 6 ? 'white' : undefined,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} style={{marginRight: 8, color: 'white'}} />
              Verifying...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MfaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idToken: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  onClearPassword: PropTypes.func.isRequired,
};
